interface Props {
  visible: boolean;
  onClose: () => void;
  onNavigate?: (target: 'scoring' | 'transparency') => void;
  onOpenDoc?: (doc: 'privacy' | 'terms' | 'subprocessors') => void;
}

export default function LegalNotice({ visible, onClose, onNavigate, onOpenDoc }: Props) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 shader:bg-black/40 shader:backdrop-blur-2xl shader:border shader:border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="font-display text-lg text-slate-900 dark:text-white">
            Mentions l&eacute;gales et avertissement
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              &Eacute;diteur du site
            </h3>
            <ul className="space-y-0.5 pl-4 list-disc">
              <li>Ludovic Mornand, exer&ccedil;ant en Entreprise Individuelle</li>
              <li>Si&egrave;ge social&nbsp;: 3 rue Pierre Valdo, 69005 Lyon, France</li>
              <li>SIREN&nbsp;: 513&nbsp;040&nbsp;576 &mdash; SIRET&nbsp;: 513&nbsp;040&nbsp;576&nbsp;00052</li>
              <li>TVA intracommunautaire&nbsp;: FR42&nbsp;513040576</li>
              <li>Directeur de la publication&nbsp;: Ludovic Mornand</li>
              <li>Contact&nbsp;: <a href="mailto:contact@blackthornsdesign.com" className="underline text-violet-600 dark:text-violet-400">contact@blackthornsdesign.com</a></li>
              <li>Adresse de production&nbsp;: <a href="https://app.loievin.fr" target="_blank" rel="noreferrer" className="underline text-violet-600 dark:text-violet-400">app.loievin.fr</a></li>
            </ul>
          </section>

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              H&eacute;bergement
            </h3>
            <p>
              Le site est h&eacute;berg&eacute; par <strong>IONOS SARL</strong>, 7 place de la Gare, 57200 Sarreguemines, France. Infrastructure et donn&eacute;es de l'application h&eacute;berg&eacute;es en Union europ&eacute;enne.
            </p>
            <p className="mt-2">
              La base de donn&eacute;es et le stockage des fichiers utilisateurs (<strong>PocketBase</strong>) sont h&eacute;berg&eacute;s sur ce m&ecirc;me serveur, en France, sur disque chiffr&eacute;. Les mod&egrave;les d'intelligence artificielle utilis&eacute;s pour l'analyse sont fournis par <strong>Mistral AI</strong>, soci&eacute;t&eacute; fran&ccedil;aise, h&eacute;berg&eacute;s en Union europ&eacute;enne.
            </p>
          </section>

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              Nature du service
            </h3>
            <p>
              Evinly est un outil d'aide &agrave; la d&eacute;cision utilisant l'intelligence
              artificielle pour &eacute;valuer la conformit&eacute; de contenus publicitaires &agrave;
              la l&eacute;gislation fran&ccedil;aise encadrant la publicit&eacute; des boissons
              alcoolis&eacute;es (dite &laquo; Loi &Eacute;vin &raquo;, articles L3323-2 &agrave;
              L3323-6 du Code de la sant&eacute; publique).
            </p>
          </section>

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              Absence de valeur juridique
            </h3>
            <p>
              Les analyses,{' '}
              {onNavigate ? (
                <button
                  onClick={() => { onClose(); onNavigate('scoring'); }}
                  className="underline text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 transition-colors"
                >
                  scores et recommandations
                </button>
              ) : (
                'scores et recommandations'
              )}{' '}
              produits par Evinly sont
              fournis <strong>&agrave; titre purement indicatif</strong>. Ils ne constituent en aucun
              cas un avis juridique, une consultation d'avocat, ni une garantie de conformit&eacute;
              l&eacute;gale. Aucun r&eacute;sultat g&eacute;n&eacute;r&eacute; par l'application ne
              saurait se substituer &agrave; l'expertise d'un professionnel du droit.
            </p>
          </section>

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              Obligation de validation par un professionnel
            </h3>
            <p>
              Toute d&eacute;cision li&eacute;e &agrave; la publication, la diffusion ou la
              modification d'un contenu publicitaire portant sur des boissons alcoolis&eacute;es
              doit &ecirc;tre valid&eacute;e par un avocat sp&eacute;cialis&eacute; en droit de la
              publicit&eacute; ou en droit de la sant&eacute; publique. Cela s'applique quel que soit
              votre statut : marque, agence de communication, influenceur, distributeur ou tout
              autre acteur de la fili&egrave;re.
            </p>
          </section>

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              Limitation de responsabilit&eacute;
            </h3>
            <p>
              L'&eacute;diteur d'Evinly ne pourra en aucun cas &ecirc;tre tenu responsable des
              cons&eacute;quences directes ou indirectes r&eacute;sultant de l'utilisation des
              r&eacute;sultats fournis par l'application, y compris en cas de poursuites judiciaires,
              de sanctions administratives, ou de tout autre pr&eacute;judice li&eacute; &agrave; la
              non-conformit&eacute; d'un contenu &agrave; la l&eacute;gislation en vigueur.
            </p>
            <p className="mt-2">
              L'utilisateur assume l'enti&egrave;re responsabilit&eacute; de l'usage qu'il fait des
              analyses et recommandations fournies par Evinly.
            </p>
          </section>

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              Donn&eacute;es juridiques
            </h3>
            <p>
              Les textes de loi et jurisprudences cit&eacute;s dans les r&eacute;sultats proviennent
              de l'API L&eacute;gifrance, mise &agrave; disposition par la DILA (Direction de
              l'information l&eacute;gale et administrative) sous{' '}
              <a
                href="https://www.etalab.gouv.fr/wp-content/uploads/2017/04/ETALAB-Licence-Ouverte-v2.0.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-violet-600 dark:text-violet-400"
              >
                Licence Ouverte 2.0
              </a>.
            </p>
            <p className="mt-2">
              Ces donn&eacute;es sont fournies &agrave; titre informatif et <strong>ne sont pas
              juridiquement opposables</strong>. Seuls les textes publi&eacute;s au Journal officiel
              de la R&eacute;publique fran&ccedil;aise, dans leur version PDF sign&eacute;e, font foi.
            </p>
          </section>

          <section>
            <h3 className="font-display text-slate-900 dark:text-white mb-2">
              Intelligence artificielle
            </h3>
            <p>
              Evinly utilise des{' '}
              {onNavigate ? (
                <button
                  onClick={() => { onClose(); onNavigate('transparency'); }}
                  className="underline text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 transition-colors"
                >
                  mod&egrave;les de langage (IA)
                </button>
              ) : (
                'mod\u00e8les de langage (IA)'
              )}{' '}
              pour produire ses analyses. Ces
              mod&egrave;les, bien qu'avanc&eacute;s, peuvent commettre des erreurs
              d'interpr&eacute;tation, des omissions ou des inexactitudes. Les r&eacute;sultats
              doivent toujours &ecirc;tre examin&eacute;s avec discernement et recoup&eacute;s avec
              l'avis d'un professionnel qualifi&eacute;.
            </p>
          </section>

          {onOpenDoc && (
            <section className="border-t border-slate-200 dark:border-slate-700 pt-4">
              <h3 className="font-display text-slate-900 dark:text-white mb-2">
                Documents juridiques
              </h3>
              <p className="mb-3">
                Pour plus de d&eacute;tails sur vos droits, les donn&eacute;es trait&eacute;es et les conditions
                d'utilisation du service, consultez les documents suivants&nbsp;:
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { onClose(); onOpenDoc('privacy'); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Politique de confidentialit&eacute;
                </button>
                <button
                  onClick={() => { onClose(); onOpenDoc('terms'); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Conditions G&eacute;n&eacute;rales d'Utilisation
                </button>
                <button
                  onClick={() => { onClose(); onOpenDoc('subprocessors'); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Sous-traitants
                </button>
              </div>
            </section>
          )}

        </div>

        <div className="p-6 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="font-display w-full py-3 rounded-xl border border-slate-300 dark:border-slate-600 shader:border-white/15 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
